// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import React, { useState } from 'react';
import { DataTable, DataTableExpandedRows } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import { WorkingHoursSideBarFilter } from './filters/working-hours-side-bar-filter';
import { getWorkingHoursOperations } from './operations/get-working-hours-operations';
import { UserSummaryType } from '../../../../../api/types/user/user-summary.type';
import {
  WorkingHoursEntity2,
  WorkingHoursResponse,
  WorkingHoursStatus,
} from '../../../../../api/types/lecturers/working-hours/working-hours.type';
import dateFormat from '../../../../../utils/date-format';
import { formatLecturerHourUnits } from '../../../../../utils/lecturer-hour-units';
import { WorkingHoursHistory } from './componenets/working-hours-history';
import { WorkingHoursStatusTemplate } from './componenets/status-template/Working-hours-status-template';
import { WorkingHoursLoadingTemplate } from './componenets/loading/working-hours-loading-template';
import { AddManualWorkingHoursDialog } from './componenets/add-manual-working-hours-dialog/add-manual-working-hours-dialog';
import { updateWorkingHoursStatusOperation } from './operations/update-working-hours-status-operation';
import { exportWorkingHoursToExcel } from './operations/export-working-hours-to-excel-operation';
import { TableColumnLongTextWithInformation } from '../../../../../component/table-componenets/cusom-column/table-column-long-text-with-information';

export interface WorkingHoursDatamodel {
  user: UserSummaryType;
}

export const WorkingHours: React.FC<WorkingHoursDatamodel> = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WorkingHoursResponse[]>([]);
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({});
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editData, setEditData] = useState<WorkingHoursResponse | null>(null);
  const [lastSearchRange, setLastSearchRange] = useState<{
    startDate: Date;
    endDate: Date;
  } | null>(null);

  const getWorkingHoursTimeline = (rowData: WorkingHoursResponse) => {
    const timeline: WorkingHoursEntity2[] = [
      rowData as unknown as WorkingHoursEntity2,
      ...(rowData.history || []),
    ];

    return timeline.filter(Boolean).sort((a, b) => {
      const versionDiff = (a.versionNumber ?? 0) - (b.versionNumber ?? 0);
      if (versionDiff !== 0) return versionDiff;
      return (
        new Date(a.createdAt as any).getTime() -
        new Date(b.createdAt as any).getTime()
      );
    });
  };

  const getLatestWorkingHoursRow = (
    rowData: WorkingHoursResponse,
  ): WorkingHoursResponse => {
    const timeline = getWorkingHoursTimeline(rowData);
    const latest =
      timeline[timeline.length - 1] ||
      (rowData as unknown as WorkingHoursEntity2);
    return {
      ...(latest as unknown as WorkingHoursResponse),
      history: rowData.history || [],
    };
  };

  const handleSearch = async (startDate: Date, endDate: Date) => {
    setLastSearchRange({ startDate, endDate });
    await getWorkingHoursOperations(
      user.id,
      startDate,
      endDate,
      setLoading,
      setData,
    );
  };

  const refreshData = async () => {
    if (!lastSearchRange) return;
    await getWorkingHoursOperations(
      user.id,
      lastSearchRange.startDate,
      lastSearchRange.endDate,
      setLoading,
      setData,
    );
  };

  const handleEdit = (rowData: WorkingHoursResponse) => {
    setEditData(getLatestWorkingHoursRow(rowData));
    setShowAddDialog(true);
  };

  const handleOpenAddDialog = () => {
    setEditData(null);
    setShowAddDialog(true);
  };

  const handleSetShowAddDialog = (show: boolean) => {
    setShowAddDialog(show);
    if (!show) {
      setEditData(null);
    }
  };

  const handleApprove = async (historyItem: WorkingHoursEntity2) => {
    toast.success(
      `אושר פריט מתאריך ${dateFormat.displayFormatDate(historyItem.startAt)}`,
    );
    const success = await updateWorkingHoursStatusOperation(
      historyItem.id,
      WorkingHoursStatus.Approved,
    );
    if (success) {
      await refreshData();
    }
  };

  const handleReject = async (historyItem: WorkingHoursEntity2) => {
    const success = await updateWorkingHoursStatusOperation(
      historyItem.id,
      WorkingHoursStatus.Reject,
    );
    if (success) {
      await refreshData();
    }
  };

  // Column templates for main table
  const startDateTemplate = (rowData: WorkingHoursResponse) => {
    const latest = getLatestWorkingHoursRow(rowData);
    return dateFormat.displayFormatDate(latest.startAt);
  };

  const endDateTemplate = (rowData: WorkingHoursResponse) => {
    const latest = getLatestWorkingHoursRow(rowData);
    return dateFormat.displayFormatDate(latest.endAt);
  };

  const activityTypeTemplate = (rowData: WorkingHoursResponse) => {
    const latest = getLatestWorkingHoursRow(rowData);
    return `סוג פעילות ${latest.activityTypeName}`;
  };

  const isManualTemplate = (rowData: WorkingHoursResponse) => {
    const latest = getLatestWorkingHoursRow(rowData);
    return latest.isManual ? 'ידני' : 'אוטומטי';
  };

  const rateTemplate = (rowData: WorkingHoursResponse) => {
    const latest = getLatestWorkingHoursRow(rowData);
    return `₪${latest.branchLecturersRates}`;
  };

  const breakMinutesTemplate = (rowData: WorkingHoursResponse) => {
    const latest = getLatestWorkingHoursRow(rowData);
    return latest.breakMinutes != null ? `${latest.breakMinutes}` : '-';
  };

  const branchNameTemplate = (rowData: WorkingHoursResponse) => {
    const latest = getLatestWorkingHoursRow(rowData);
    return latest.branchName?.trim() ? latest.branchName : 'לא צוין';
  };

  const courseNameTemplate = (rowData: WorkingHoursResponse) => {
    const latest = getLatestWorkingHoursRow(rowData);
    return latest.courseName?.trim() ? latest.courseName : '-';
  };

  const additionalCourseNameTemplate = (rowData: WorkingHoursResponse) => {
    const latest = getLatestWorkingHoursRow(rowData);
    return latest.additionalCourseName?.trim()
      ? latest.additionalCourseName
      : '-';
  };

  const programNameTemplate = (rowData: WorkingHoursResponse) => {
    const latest = getLatestWorkingHoursRow(rowData);
    return latest.programName?.trim() ? latest.programName : '-';
  };

  const additionalProgramNameTemplate = (rowData: WorkingHoursResponse) => {
    const latest = getLatestWorkingHoursRow(rowData);
    return latest.additionalProgramName?.trim()
      ? latest.additionalProgramName
      : '-';
  };

  const totalHoursTemplate = (rowData: WorkingHoursResponse) => {
    const latest = getLatestWorkingHoursRow(rowData);
    return formatLecturerHourUnits(
      latest.startAt,
      latest.endAt,
      latest.displayType,
    );
  };

  const editTemplate = (rowData: WorkingHoursResponse) => {
    const latest = getLatestWorkingHoursRow(rowData);
    return (
      <div
        style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
      >
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(rowData);
          }}
          tooltip="עריכה"
        />

        <Button
          icon="pi pi-times"
          severity="warning"
          text
          size="small"
          tooltip="ביטול"
          tooltipOptions={{ position: 'top' }}
          style={{ height: '24px', width: '24px' }}
          onClick={(e) => {
            e.stopPropagation();
            handleReject(latest);
          }}
        />
        <Button
          icon="pi pi-check"
          severity="success"
          text
          size="small"
          tooltip="אישור"
          tooltipOptions={{ position: 'top' }}
          style={{ height: '24px', width: '24px' }}
          onClick={(e) => {
            e.stopPropagation();
            handleApprove(latest);
          }}
        />
      </div>
    );
  };

  // Expanded row template for history
  const expandedRowTemplate = (data: WorkingHoursResponse) => (
    <WorkingHoursHistory
      data={data}
      handleApprove={handleApprove}
      handleReject={handleReject}
    />
  );

  const handleExportToExcel = () => {
    if (data.length === 0) {
      toast.warn('אין נתונים לייצוא');
      return;
    }

    exportWorkingHoursToExcel(data, getLatestWorkingHoursRow);
    toast.success('קובץ אקסל הורד בהצלחה');
  };

  const expanderTemplate = (rowData: WorkingHoursResponse) => {
    const isExpanded = expandedRows[rowData.id] !== undefined;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          const newExpandedRows = { ...expandedRows };
          if (isExpanded) {
            delete newExpandedRows[rowData.id];
          } else {
            newExpandedRows[rowData.id] = true;
          }
          setExpandedRows(newExpandedRows);
        }}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          padding: '4px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
        }}
      >
        {isExpanded ? '▼' : '▶'}
      </button>
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        gap: '16px',
      }}
    >
      {/* Sidebar Filter */}
      <div style={{ width: '300px', flexShrink: 0 }}>
        <WorkingHoursSideBarFilter onSearch={handleSearch} />
      </div>

      {/* Main Content Area */}
      <div
        style={{
          flex: 1,
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Header with Add Button */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 24px 16px 24px',
            borderBottom: '1px solid #e9ecef',
          }}
        >
          <h2 style={{ margin: 0, color: '#333' }}>שעות עבודה</h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Button
              icon="pi pi-plus"
              label="הוסף שעות עבודה"
              className="p-button-success"
              onClick={handleOpenAddDialog}
            />
            <Button
              icon="pi pi-file-excel"
              style={{
                backgroundColor: '#10b981',
                borderColor: '#10b981',
              }}
              onClick={handleExportToExcel}
              disabled={data.length === 0 || loading}
              tooltip="ייצוא לאקסל"
              tooltipOptions={{ position: 'bottom' }}
            />
          </div>
        </div>

        <div style={{ padding: '24px' }}>
          {loading ? (
            <WorkingHoursLoadingTemplate />
          ) : data.length === 0 ? (
            <p style={{ color: '#666', fontSize: '16px', textAlign: 'right' }}>
              בחר טווח תאריכים מהסינון כדי לצפות בשעות העבודה שלך
            </p>
          ) : (
            <DataTable
              value={data}
              expandedRows={expandedRows}
              onRowToggle={(e) =>
                setExpandedRows(e.data as DataTableExpandedRows)
              }
              rowExpansionTemplate={expandedRowTemplate}
              dataKey="id"
              showGridlines
              tableStyle={{ direction: 'rtl', width: '100%' }}
              emptyMessage={
                <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
              }
            >
              <Column
                expander={false}
                body={expanderTemplate}
                style={{ width: '3rem', textAlign: 'center' }}
              />
              <Column
                field="startAt"
                header="תאריך התחלה"
                body={startDateTemplate}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="startAt"
                header="שעת התחלה"
                body={(rowData: WorkingHoursResponse) => (
                  <label>
                    {dateFormat.formatTimeFromDate(
                      getLatestWorkingHoursRow(rowData).startAt,
                    )}
                  </label>
                )}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="endAt"
                header="שעת סיום"
                body={(rowData: WorkingHoursResponse) => (
                  <label>
                    {dateFormat.formatTimeFromDate(
                      getLatestWorkingHoursRow(rowData).endAt,
                    )}
                  </label>
                )}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="status"
                header="סטטוס"
                body={(rowData: WorkingHoursResponse) =>
                  WorkingHoursStatusTemplate(getLatestWorkingHoursRow(rowData))
                }
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="activityTypeName"
                header="סוג פעילות"
                body={activityTypeTemplate}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="branchName"
                header="שלוחה"
                body={branchNameTemplate}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="courseName"
                header="קורס"
                body={courseNameTemplate}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="additionalCourseName"
                header="שם קורס נוסף"
                body={additionalCourseNameTemplate}
                style={{ textAlign: 'right' }}
              />
              <Column
                field="programName"
                header="תוכנית"
                body={programNameTemplate}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="additionalProgramName"
                header="שם תוכנית נוסף"
                body={additionalProgramNameTemplate}
                style={{ textAlign: 'right' }}
              />
              <Column
                field="notes"
                header="הערות"
                body={(rowData: WorkingHoursResponse) =>
                  getLatestWorkingHoursRow(rowData).notes &&
                  getLatestWorkingHoursRow(rowData).notes!.trim() ? (
                    <TableColumnLongTextWithInformation
                      text={getLatestWorkingHoursRow(rowData).notes!}
                      uniqueId={getLatestWorkingHoursRow(rowData).id}
                      maxLength={40}
                    />
                  ) : (
                    <span>-</span>
                  )
                }
                style={{ textAlign: 'right', maxWidth: '240px' }}
              />
              <Column
                field="isManual"
                header="סוג"
                body={isManualTemplate}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="branchLecturersRates"
                header="תעריף"
                body={rateTemplate}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="breakMinutes"
                header="דקות הפסקה"
                body={breakMinutesTemplate}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                header="פעולות"
                body={editTemplate}
                style={{ textAlign: 'center', width: '80px' }}
              />
              <Column
                header="סה״כ"
                body={totalHoursTemplate}
                style={{ textAlign: 'right' }}
              />
            </DataTable>
          )}
        </div>
      </div>

      <AddManualWorkingHoursDialog
        userId={user.id}
        showAddDialog={showAddDialog}
        setShowAddDialog={handleSetShowAddDialog}
        editData={editData}
        onSuccess={refreshData}
      />
    </div>
  );
};
